// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//


import { getAlliedUniversityChildrenService, GetPagesByParentApiParameter } from "@awc/App/Services/GetAlliedUniversityChildrenService";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import { PagesCollectionModel } from "@insite/client-framework/Services/ContentService";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { PageModel } from "@insite/client-framework/Types/PageProps";

export interface PagesTreeModel {
    pages: PageModel[];
    pagination: PaginationModel;
}

type HandlerType = ApiHandler<
    GetPagesByParentApiParameter & HasOnSuccess<ServiceResult<PagesTreeModel>> & HasOnError<string>,
    ServiceResult<PagesTreeModel>
>;
export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = { ...props.parameter };
    (props as any).key = props.apiParameter.parentId;
};

export const DispatchBeginGetPagesContent: HandlerType = props => {
    props.dispatch({
        type: "xPagesContent/BeginLoadPagesContent",
        key: (props as any).key,
    } as any);
};

export const GetPagesContent: HandlerType = async props => {
    const { onSuccess, onError, ...apiParameter } = props.apiParameter;
    props.apiResult = await getAlliedUniversityChildrenService(apiParameter);
};

export const DispatchCompleteGetPagesContent: HandlerType = props => {
    props.dispatch({
        type: "xPagesContent/CompleteLoadPagesContent",
        data: (props.apiResult as any)?.result?.pages,
        key: (props as any).key,
        isMainRoot: true,
        state: props.getState(),
    } as any);
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        props.dispatch({
            type: "xPagesContent/FailedToLoadPagesContent",
            key: (props as any).key,
        } as any);
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage!);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const chain = [
    PopulateApiParameter,
    DispatchBeginGetPagesContent,
    GetPagesContent,
    DispatchCompleteGetPagesContent,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
];
const getPagesByParentId = createHandlerChainRunner(chain, "GetPagesByParentId");

export default getPagesByParentId;
